import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, Building2, Mail, Phone, ArrowRight, Loader2, ArrowLeft, Clock, CheckCircle2, Users, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "/admixio-logo.png";

const RegisterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    name: '',
    role: '',
    company_name: '',
    phone: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.email) {
      toast.error('Email is required');
      return;
    }

    setLoading(true);
    try {
      await register(formData);
      
      // For agents and institutions, show pending approval message
      if (formData.role !== 'super_admin') {
        setSubmitted(true);
        toast.success('Registration submitted! Awaiting admin approval.');
      } else {
        toast.success('Registration successful!');
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Registration failed';
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg || e).join(', '));
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show success message after registration
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0D0B1E] flex items-center justify-center p-6 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/10 blur-[120px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-[#8B5CF6]/10 blur-[100px]" />
        </div>

        <div className="relative w-full max-w-md z-10">
          <div className="glass-strong border border-[#2D2654] rounded-2xl p-8 text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#00D4FF]/20 to-[#8B5CF6]/20 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thank You for Registering!
            </h1>
            
            <p className="text-[#B8B4CE] mb-6">
              Our team will review your application and contact you shortly. You will receive portal access once your account is approved by the admin.
            </p>

            <div className="bg-[#0D0B1E] rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                <span className="text-sm text-white">Application submitted</span>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-sm text-[#B8B4CE]">Awaiting admin approval</span>
              </div>
              <div className="flex items-center gap-3 text-[#6B678A]">
                <Users className="w-5 h-5" />
                <span className="text-sm">Account activation</span>
              </div>
            </div>

            <p className="text-xs text-[#6B678A] mb-6">
              You'll receive an email notification once your account is activated.
            </p>

            <Link to="/">
              <Button className="w-full btn-gradient">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0B1E] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full bg-[#00D4FF]/10 blur-[120px] animate-breathe" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] rounded-full bg-[#8B5CF6]/10 blur-[100px] animate-breathe" style={{ animationDelay: '2s' }} />
      </div>

      {/* Particles */}
      <div className="particles">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={`particle-${i}`}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              bottom: '-10px',
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      {/* Register Card */}
      <div className="relative w-full max-w-lg z-10" data-testid="register-container">
        <div className="glass-strong border border-[#2D2654] shadow-[0_8px_32px_rgba(0,212,255,0.15)] rounded-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Link to="/">
              <img 
                src={LOGO_URL}
                alt="Admixio" 
                className="h-14 rounded-lg"
              />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Join Admixio
            </h1>
            <p className="text-[#B8B4CE] text-sm">
              Create your account to start recruiting globally
            </p>
          </div>

          {/* Info Banner */}
          <div className="bg-gradient-to-r from-[#00D4FF]/10 to-[#8B5CF6]/10 border border-[#2D2654] rounded-xl p-4 mb-6 flex items-start gap-3">
            <Shield className="w-5 h-5 text-[#00D4FF] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-[#B8B4CE]">
              Your registration will be reviewed by our admin team. You'll receive an email once your account is approved.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-[#B8B4CE]">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B678A]" strokeWidth={1.5} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 bg-[#0D0B1E] border-[#2D2654] text-white placeholder:text-[#6B678A] focus:border-[#00D4FF]"
                  readOnly={!!location.state?.email}
                  data-testid="register-email-input"
                />
              </div>
            </div>

            {/* Full Name */}
            <div className="space-y-2">
              <label className="text-sm text-[#B8B4CE]">Full Name *</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B678A]" strokeWidth={1.5} />
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="pl-10 bg-[#0D0B1E] border-[#2D2654] text-white placeholder:text-[#6B678A] focus:border-[#00D4FF]"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <label className="text-sm text-[#B8B4CE]">I am a *</label>
              <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                <SelectTrigger 
                  className="bg-[#0D0B1E] border-[#2D2654] text-white focus:border-[#00D4FF] focus:ring-1 focus:ring-[#00D4FF]"
                  data-testid="register-role-select"
                >
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent className="bg-[#141029] border-[#2D2654]">
                  <SelectItem 
                    value="agent"
                    className="text-white focus:bg-[#2D2654] focus:text-white"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#00D4FF]" />
                      Recruitment Partner (Agent)
                    </div>
                  </SelectItem>
                  <SelectItem 
                    value="institution"
                    className="text-white focus:bg-[#2D2654] focus:text-white"
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-[#8B5CF6]" />
                      Institution
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <label className="text-sm text-[#B8B4CE]">Company / Institution Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B678A]" strokeWidth={1.5} />
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Acme Education Ltd"
                  className="pl-10 bg-[#0D0B1E] border-[#2D2654] text-white placeholder:text-[#6B678A] focus:border-[#00D4FF]"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="text-sm text-[#B8B4CE]">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B678A]" strokeWidth={1.5} />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="pl-10 bg-[#0D0B1E] border-[#2D2654] text-white placeholder:text-[#6B678A] focus:border-[#00D4FF]"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient mt-6"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Submit Registration
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#2D2654]">
            <p className="text-center text-sm text-[#6B678A]">
              By registering, you agree to our{' '}
              <a href="#" className="text-[#00D4FF] hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-[#00D4FF] hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-[#6B678A] hover:text-[#B8B4CE] transition-colors inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Already have an account? Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
